import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { DateFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SvbrgDocumentListItem } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import {
  cellFactory,
  DataGridConfig,
  DataGridLinkVariant,
  dateFormatterFactory,
  gridColDefFactory,
  LinkCell,
  UseDataGridConfig,
} from '~/components/DataGridCustom';
import { I18nCell } from '~/components/DataGridCustom/cells/I18nCell/I18nCell';

import { ProcurementKnowledgeGridAction } from './components/ProcurementKnowledgeGridAction/ProcurementKnowledgeGridAction';
import { ProcurementKnowledgeDataGridColumn } from './ProcurementKnowledgeList.types';

export const useProcurementKnowledgeDataGridConfig: UseDataGridConfig<SvbrgDocumentListItem> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation('knowledgeRepository', { keyPrefix: 'ProcurementKnowledge.column' });

  const { hasPermission } = usePermissions();
  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });

    const config: DataGridConfig<SvbrgDocumentListItem> = {
      columns: [
        // file title
        createColumn({
          field: ProcurementKnowledgeDataGridColumn.TITLE,
          minWidth: 200,
          maxWidth: 400,
        }),

        // file description
        createColumn({
          field: ProcurementKnowledgeDataGridColumn.DESCRIPTION,
          maxWidth: 400,
        }),

        // file id -> resolves to file name
        createColumn({
          field: ProcurementKnowledgeDataGridColumn.FILE_NAME,
          minWidth: 400,
        }),

        // file type (e.g. PDF, DOCX)
        createColumn({
          field: ProcurementKnowledgeDataGridColumn.TYPE,
          renderCell: (col) => col.value?.split('/')[1],
          minWidth: 100,
        }),

        // file variant -> resolves to translation of file variant, e.g. "Procurement Policy"
        createColumn({
          field: ProcurementKnowledgeDataGridColumn.VARIANT,
          minWidth: 265,
          renderCell: cellFactory(I18nCell, {
            ns: I18nNamespace.knowledgeRepository,
            keyPrefix: 'ProcurementKnowledgeVariant',
          }),
        }),

        // document language -> resolves to language translation, e.g. "English"
        createColumn({
          field: ProcurementKnowledgeDataGridColumn.LANGUAGE,
          minWidth: 265,
          renderCell: cellFactory(I18nCell, {
            ns: I18nNamespace.common,
            keyPrefix: 'language',
          }),
        }),

        // document update timestamp
        createColumn({
          field: ProcurementKnowledgeDataGridColumn.UPDATED_AT,
          headerName: tColumn('updatedAt'),
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT_TIME }),
          minWidth: 150,
        }),

        // URL of document -> TODO: server returns empty, we might generate a falcon link on the client based on file_id
        createColumn({
          field: ProcurementKnowledgeDataGridColumn.URL,
          minWidth: 265,
          renderCell: cellFactory(LinkCell),

          valueGetter: (_value, row) => ({
            variant: DataGridLinkVariant.STANDARD,
            to: row.url,
            label: row.url,
            target: '_blank',
          }),
        }),

        // fixes actions column, right now edit button
        createColumn({
          field: ProcurementKnowledgeDataGridColumn.ACTIONS,
          width: 64,
          minWidth: 64,
          maxWidth: 64,
          type: 'actions',
          headerName: '',
          sortable: false,
          align: 'center',
          disableExport: true,
          getActions: (params) => {
            const id = params.row.id;
            return hasPermission(Permission.KnowledgeEdit)
              ? [<ProcurementKnowledgeGridAction id={id} key={`edit-${id}`} />]
              : [];
          },
        }),
      ],
      exportModel: {},
    } as DataGridConfig<SvbrgDocumentListItem>;
    return config;
  }, [t, tColumn, hasPermission]);
};
